import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useEmpresaSelector } from "../context/EmpresaSelectorContext";
import { useEffect, useRef, useState } from "react";
import { getRolDisplayName } from "../utils/constants";
import "../styles/styles.css";

export default function Sidebar({ isOpen }) {
  const { usuario, logout, isAdmin, isEmpresaAdmin } = useAuth();
  const { empresaSeleccionada, cambiarEmpresa, empresas, puedeCambiarEmpresa, loading } = useEmpresaSelector();
  const location = useLocation();

  const [showContabilidadSubmenu, setShowContabilidadSubmenu] = useState(false);
  const [showInventarioSubmenu, setShowInventarioSubmenu] = useState(false);
  const [showReporteSubmenu, setShowReporteSubmenu] = useState(false);
  const [showAdminSubmenu, setShowAdminSubmenu] = useState(false);
  const contabilidadSubmenuRef = useRef(null);
  const inventarioSubmenuRef = useRef(null);
  const reporteSubmenuRef = useRef(null);
  const adminSubmenuRef = useRef(null);

  useEffect(() => {
    if (location.pathname.startsWith("/dashboard/contabilidad")) {
      setShowContabilidadSubmenu(true);
    }
    if (location.pathname.startsWith("/dashboard/inventario")) {
      setShowInventarioSubmenu(true);
    }
    if (location.pathname.startsWith("/dashboard/reporte")) {
      setShowReporteSubmenu(true);
    }
    if (location.pathname.startsWith("/dashboard/admin")) {
      setShowAdminSubmenu(true);
    }
  }, [location.pathname]);

  if (!usuario || location.pathname === "/login") return null;

  const handleLogout = () => {
    logout();
  };

  const toggleContabilidad = () => {
    setShowContabilidadSubmenu((prev) => !prev);
  };

  const toggleInventario = () => {
    setShowInventarioSubmenu((prev) => !prev);
  };

  const toggleReporte = () => {
    setShowReporteSubmenu((prev) => !prev);
  };

  const toggleAdmin = () => {
    setShowAdminSubmenu((prev) => !prev);
  };

  const links = [
    { to: "/", label: "Home", roles: ["conexadmin", "administrador", "trabajadores", "cliente"] },
    { to: "/dashboard/inventario", label: "Inventario", roles: ["conexadmin", "administrador", "trabajadores"] },
    { to: "/dashboard/cierrecaja", label: "Ingreso Diario", roles: ["conexadmin", "administrador", "trabajadores"] },
    { to: "/dashboard/contabilidad", label: "Contabilidad", roles: ["conexadmin", "administrador", "trabajadores"], isDropdown: true },
    { to: "/dashboard/reporte", label: "Reporte", roles: ["conexadmin", "administrador"], isDropdown: true },
    { to: "/dashboard/admin", label: "Administración", roles: ["conexadmin"], isDropdown: true },
  ];

  const contabilidadSubLinks = [
    { to: "/dashboard/contabilidad/ingresos", label: "Cuentas por Cobrar", roles: ["conexadmin", "administrador", "trabajadores"] },
    { to: "/dashboard/contabilidad/egresos", label: "Cuentas por Pagar", roles: ["conexadmin", "administrador", "trabajadores"] },
    { to: "/dashboard/contabilidad/calculo-productos", label: "Cálculo de Productos", roles: ["conexadmin", "administrador", "trabajadores"] },
    { to: "/dashboard/contabilidad/resumen", label: "Resumen", roles: ["conexadmin", "administrador", "trabajadores"] },
  ];

  const inventarioSubLinks = [
    { to: "/dashboard/inventario/productos", label: "Productos", roles: ["conexadmin", "administrador", "trabajadores"] },
    { to: "/dashboard/inventario/stock", label: "Control de Stock", roles: ["conexadmin", "administrador", "trabajadores"] },
  ];

  const reporteSubLinks = [
    { to: "/dashboard/reporte/cuentas", label: "Cuentas", roles: ["conexadmin", "administrador"] },
    { to: "/dashboard/reporte/patrimonio", label: "Patrimonio", roles: ["conexadmin", "administrador"] },
    { to: "/dashboard/reporte/flujo-caja", label: "Flujo de Caja", roles: ["conexadmin", "administrador"] },
    { to: "/dashboard/reporte/resumen-total", label: "Resumen Total", roles: ["conexadmin", "administrador"] },
  ];

  const adminSubLinks = [
    { to: "/dashboard/admin/usuarios", label: "Usuarios", roles: ["conexadmin"] },
    { to: "/dashboard/admin/empresas", label: "Empresas", roles: ["conexadmin"] },
  ];

  return (
    <aside className={`menu has-bg-darkblue is-flex is-flex-direction-column fullheight custom-sidebar ${isOpen ? 'is-active' : ''}`}>
      <div>
        <div className="has-text-centered mb-4">
          <img src="/blanco.png" alt="Logo" style={{ maxWidth: "120px", marginTop: "1rem" }} />
          <p className="has-text-gold mt-3 is-size-6 has-text-weight-semibold">
            Bienvenido al sistema, {usuario?.nombres || "Usuario"} - {getRolDisplayName(usuario?.rol)}
          </p>
          {usuario?.empresa && (
            <p className="has-text-white mt-2 is-size-7">
              Empresa: {usuario.empresa.nombre}
            </p>
          )}
        </div>

        {puedeCambiarEmpresa && empresas && empresas.length > 0 ? (
          <div className="mb-4 px-3">
            <p className="has-text-gold has-text-centered is-size-7 mb-2">Empresa Seleccionada</p>
            <div className="select is-fullwidth is-small">
              <select
                value={empresaSeleccionada?.id === 'all' ? 'all' : (empresaSeleccionada?.id?.toString() || '')}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'all') {
                    cambiarEmpresa({ id: 'all', nombre: 'Todas las empresas' });
                  } else {
                    const selectedEmpresa = empresas.find(emp => emp.id?.toString() === value);
                    if (selectedEmpresa) {
                      cambiarEmpresa(selectedEmpresa);
                    } else {
                    }
                  }
                }}
                className="has-text-darkblue"
              >
                <option value="all">Todas las empresas</option>
                {empresas.map(empresa => (
                  <option key={empresa.id} value={empresa.id?.toString()}>
                    {empresa.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}

        <p className="menu-label has-text-gold has-text-centered">Menú</p>
        <ul className="menu-list">
          {links
            .filter((link) => link.roles.some(role => role.toLowerCase() === usuario.rol?.toLowerCase()))
            .map((link) => (
              <li key={link.to}>
                {link.isDropdown ? (
                  link.label === "Contabilidad" ? (
                    <>
                      <button
                        onClick={toggleContabilidad}
                        className="button is-fullwidth is-rounded mb-2 has-text-left has-text-white hover-gold"
                      >
                        {link.label}
                      </button>
                      <div
                        ref={contabilidadSubmenuRef}
                        className={`submenu-container ${showContabilidadSubmenu ? "submenu-open" : "submenu-closed"} ml-4`}
                      >
                        <ul>
                          {contabilidadSubLinks
                            .filter((sublink) => sublink.roles.some(role => role.toLowerCase() === usuario.rol?.toLowerCase()))
                            .map((sublink) => (
                              <li key={sublink.to}>
                                <NavLink
                                  to={sublink.to}
                                  className={({ isActive }) =>
                                    `button is-small is-fullwidth is-rounded mb-1 has-text-left ${isActive ? "has-bg-gold has-text-darkblue" : "has-text-white"
                                    } hover-gold`
                                  }
                                >
                                  {sublink.label}
                                </NavLink>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </>
                  ) : link.label === "Inventario" ? (
                    <>
                      <button
                        onClick={toggleInventario}
                        className="button is-fullwidth is-rounded mb-2 has-text-left has-text-white hover-gold"
                      >
                        {link.label}
                      </button>
                      <div
                        ref={inventarioSubmenuRef}
                        className={`submenu-container ${showInventarioSubmenu ? "submenu-open" : "submenu-closed"} ml-4`}
                      >
                        <ul>
                          {inventarioSubLinks
                            .filter((sublink) => sublink.roles.some(role => role.toLowerCase() === usuario.rol?.toLowerCase()))
                            .map((sublink) => (
                              <li key={sublink.to}>
                                <NavLink
                                  to={sublink.to}
                                  className={({ isActive }) =>
                                    `button is-small is-fullwidth is-rounded mb-1 has-text-left ${isActive ? "has-bg-gold has-text-darkblue" : "has-text-white"
                                    } hover-gold`
                                  }
                                >
                                  {sublink.label}
                                </NavLink>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </>
                  ) : link.label === "Reporte" ? (
                    <>
                      <button
                        onClick={toggleReporte}
                        className="button is-fullwidth is-rounded mb-2 has-text-left has-text-white hover-gold"
                      >
                        {link.label}
                      </button>
                      <div
                        ref={reporteSubmenuRef}
                        className={`submenu-container ${showReporteSubmenu ? "submenu-open" : "submenu-closed"} ml-4`}
                      >
                        <ul>
                          {reporteSubLinks
                            .filter((sublink) => sublink.roles.some(role => role.toLowerCase() === usuario.rol?.toLowerCase()))
                            .map((sublink) => (
                              <li key={sublink.to}>
                                <NavLink
                                  to={sublink.to}
                                  className={({ isActive }) =>
                                    `button is-small is-fullwidth is-rounded mb-1 has-text-left ${isActive ? "has-bg-gold has-text-darkblue" : "has-text-white"
                                    } hover-gold`
                                  }
                                >
                                  {sublink.label}
                                </NavLink>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={toggleAdmin}
                        className="button is-fullwidth is-rounded mb-2 has-text-left has-text-white hover-gold"
                      >
                        {link.label}
                      </button>
                      <div
                        ref={adminSubmenuRef}
                        className={`submenu-container ${showAdminSubmenu ? "submenu-open" : "submenu-closed"} ml-4`}
                      >
                        <ul>
                          {adminSubLinks
                            .filter((sublink) => sublink.roles.some(role => role.toLowerCase() === usuario.rol?.toLowerCase()))
                            .map((sublink) => (
                              <li key={sublink.to}>
                                <NavLink
                                  to={sublink.to}
                                  onClick={() => console.log('🔗 Navegando a:', sublink.to)}
                                  className={({ isActive }) =>
                                    `button is-small is-fullwidth is-rounded mb-1 has-text-left ${isActive ? "has-bg-gold has-text-darkblue" : "has-text-white"
                                    } hover-gold`
                                  }
                                >
                                  {sublink.label}
                                </NavLink>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </>
                  )
                ) : (
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `button is-fullwidth is-rounded mb-2 has-text-left ${isActive ? "has-bg-gold has-text-darkblue" : "has-text-white"
                      } hover-gold`
                    }
                  >
                    {link.label}
                  </NavLink>
                )}
              </li>
            ))}
        </ul>
      </div>

      <div className="mt-4 pt-4 has-border-top">
        <button
          className="button is-fullwidth is-rounded has-bg-gold has-text-darkblue"
          onClick={handleLogout}
        >
          Cerrar sesión
        </button>
      </div>
    </aside>
  );
}
